import { lazy, Suspense, type ComponentType } from "react";
import { Routes, Route, Outlet } from "react-router-dom";
import { Loader2 } from "lucide-react";
import ProtectedRoute from "@/components/ProtectedRoute";
import FeatureRoute from "@/components/FeatureRoute";
import { LegacyTokenRedirect } from "@/hooks/useHashToken";

// Public / SEO / guest pages stay eagerly imported — they're the initial paint
// and must render server-side without a chunk fetch.
import Landing from "./pages/Landing";
import SignIn from "./pages/SignIn";
import SignUp from "./pages/SignUp";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import HowWeOrderTutors from "./pages/HowWeOrderTutors";
import CookiePolicy from "./pages/CookiePolicy";
import LegalNotice from "./pages/LegalNotice";
import FAQ from "./pages/FAQ";
import CommunityGuidelines from "./pages/CommunityGuidelines";
import Careers from "./pages/Careers";
import Help from "./pages/Help";
import About from "./pages/About";
import Blog from "./pages/Blog";
import BlogArticle from "./pages/BlogArticle";
import Authors from "./pages/Authors";
import AuthorPage from "./pages/AuthorPage";
import PublicTutorProfile from "./pages/PublicTutorProfile";
import Contact from "./pages/Contact";
import JynioApp from "./pages/apps/JynioApp";
import FlashcardsApp from "./pages/apps/FlashcardsApp";
import PuzzleApp from "./pages/apps/PuzzleApp";
import ChatApp from "./pages/apps/ChatApp";
import BecomeTutorLanguage from "./pages/BecomeTutorLanguage";
import FindTutorLanguage from "./pages/FindTutorLanguage";

const RouteFallback = () => (
  <div className="flex min-h-screen items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
  </div>
);

// Auth-walled tree — lazily code-split so anonymous visitors don't download the
// student/tutor/onboarding bundles on first paint (#191). The eagerly imported,
// server-rendered public pages have NO Suspense boundary above them. That is
// deliberate: React hydrates a server-rendered Suspense boundary in a later,
// low-priority pass, after the shell's effects have run, so LocaleSync's
// language switch and the Me query's answer used to reach the page before it
// had hydrated and forced a full client re-render. A chunk that fails to load
// is almost always a tab left open across a deploy: lib/chunkReload.ts reloads
// it onto the new build, and the ErrorBoundary around AppRoutes (AppShell.tsx)
// says so meanwhile.
//
// The signed-in pages share ONE boundary (SignedInPages below), not one each. With
// one each, every move from a page to the next mounted a brand-new boundary: the
// old page, header included, was swapped for a full-screen spinner until the next
// chunk arrived, and the header came back in a fresh layout (#1428). With one
// boundary that stays mounted, a navigation inside a transition keeps the old page
// on screen until the new one is ready.
function lazyPage(load: () => Promise<{ default: ComponentType }>) {
  return lazy(load);
}

const SignedInPages = () => (
  <Suspense fallback={<RouteFallback />}>
    <Outlet />
  </Suspense>
);

const StudentClassroom = lazyPage(() => import("./pages/student/Classroom"));
const StudentCourses = lazyPage(() => import("./pages/student/Courses"));
const StudentFlashcards = lazyPage(() => import("./pages/student/Flashcards"));
const FlashcardsBrowse = lazyPage(() => import("./pages/student/FlashcardsBrowse"));
const StudentMarketplace = lazyPage(() => import("./pages/student/Marketplace"));
const StudentCalendarPage = lazyPage(() => import("./pages/student/Calendar"));
const StudentSettings = lazyPage(() => import("./pages/student/Settings"));
const StudentLiveSession = lazyPage(() => import("./pages/student/LiveSession"));
const LessonDone = lazyPage(() => import("./pages/LessonDone"));
const StudentPracticeRoom = lazyPage(() => import("./pages/student/PracticeRoom"));
const StudentBuyCredits = lazyPage(() => import("./pages/student/BuyCredits"));
const StudentFindTutors = lazyPage(() => import("./pages/student/FindTutors"));
const StudentCoursePlayer = lazyPage(() => import("./pages/student/CoursePlayer"));
const TutorProfile = lazyPage(() => import("./pages/student/TutorProfile"));
const StudentPuzzles = lazyPage(() => import("./pages/student/Puzzles"));
const StudentPuzzlePlayer = lazyPage(() => import("./pages/student/PuzzlePlayer"));
const TutorClassroom = lazyPage(() => import("./pages/tutor/Classroom"));
const TutorCourses = lazyPage(() => import("./pages/tutor/Courses"));
const TutorCalendarPage = lazyPage(() => import("./pages/tutor/Calendar"));
const TutorSettings = lazyPage(() => import("./pages/tutor/Settings"));
const TutorLiveSession = lazyPage(() => import("./pages/tutor/LiveSession"));
const TutorPracticeRoom = lazyPage(() => import("./pages/tutor/PracticeRoom"));
const TutorEarnings = lazyPage(() => import("./pages/tutor/Earnings"));
const TutorTaxInformation = lazyPage(() => import("@/pages/tutor/TaxInformation"));
const TutorReviews = lazyPage(() => import("./pages/tutor/Reviews"));
const TutorStudents = lazyPage(() => import("./pages/tutor/Students"));
const TutorStudentPractice = lazyPage(() => import("./pages/tutor/StudentPractice"));
const TutorAffiliate = lazyPage(() => import("./pages/tutor/Affiliate"));
const TutorCreateCourse = lazyPage(() => import("./pages/tutor/CreateCourse"));
const TutorResources = lazyPage(() => import("./pages/tutor/Resources"));
const TutorPlayground = lazyPage(() => import("./pages/tutor/Playground"));
const TutorCommunity = lazyPage(() => import("./pages/tutor/Community"));
const TutorCommunityThread = lazyPage(() => import("./pages/tutor/CommunityThread"));
const StudentOnboarding = lazyPage(() => import("./pages/onboarding/StudentOnboarding"));
const StudentPreferences = lazyPage(() => import("./pages/onboarding/StudentPreferences"));
const TutorOnboarding = lazyPage(() => import("./pages/onboarding/TutorOnboarding"));
const PendingApproval = lazyPage(() => import("./pages/onboarding/PendingApproval"));
const BookClass = lazyPage(() => import("./pages/BookClass"));
const Community = lazyPage(() => import("./pages/Community"));
const Messages = lazyPage(() => import("./pages/Messages"));

const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<Landing />} />
    <Route path="/privacy" element={<PrivacyPolicy />} />
    <Route path="/terms" element={<TermsOfService />} />
    <Route path="/how-we-order-tutors" element={<HowWeOrderTutors />} />
    <Route path="/cookies" element={<CookiePolicy />} />
    <Route path="/legal-notice" element={<LegalNotice />} />
    <Route path="/faq" element={<FAQ />} />
    <Route path="/community-guidelines" element={<CommunityGuidelines />} />
    <Route path="/careers" element={<Careers />} />
    <Route path="/help" element={<Help />} />
    <Route path="/about" element={<About />} />
    <Route path="/blog" element={<Blog />} />
    <Route path="/blog/:slug" element={<BlogArticle />} />
    <Route path="/authors" element={<Authors />} />
    <Route path="/authors/:slug" element={<AuthorPage />} />
    {/* A tutor's public page (#669), server-rendered by TutorPagesController. */}
    <Route path="/tutors/:slug" element={<PublicTutorProfile />} />
    <Route path="/contact" element={<Contact />} />
    <Route path="/apps/jynio" element={<JynioApp />} />
    <Route path="/apps/flashcards" element={<FlashcardsApp />} />
    <Route path="/apps/puzzle" element={<PuzzleApp />} />
    <Route path="/apps/chat" element={<ChatApp />} />
    <Route path="/become-a-tutor/:language" element={<BecomeTutorLanguage />} />
    <Route path="/find-a-tutor/:language" element={<FindTutorLanguage />} />
    {/* Locale-prefixed variants (/fr/find-a-tutor/spanish) — same pages, localized Sanity content. */}
    <Route path="/:locale/become-a-tutor/:language" element={<BecomeTutorLanguage />} />
    <Route path="/:locale/find-a-tutor/:language" element={<FindTutorLanguage />} />

    <Route path="/signin" element={<ProtectedRoute mode="guest"><SignIn /></ProtectedRoute>} />
    <Route path="/signup" element={<ProtectedRoute mode="guest"><SignUp /></ProtectedRoute>} />
    <Route path="/forgot-password" element={<ProtectedRoute mode="guest"><ForgotPassword /></ProtectedRoute>} />
    {/* The token rides after `#` so no server, log or Referer sees it (#1079). The
        :token routes keep links already in inboxes working: they swap the address
        for the `#` form. */}
    <Route path="/reset-password" element={<ProtectedRoute mode="guest"><ResetPassword /></ProtectedRoute>} />
    <Route path="/reset-password/:token" element={<LegacyTokenRedirect to="/reset-password" />} />
    {/* Works signed-in or out — the token is the proof, so no guest/auth gate. */}
    <Route path="/verify-email" element={<VerifyEmail />} />
    <Route path="/verify-email/:token" element={<LegacyTokenRedirect to="/verify-email" />} />

    <Route element={<SignedInPages />}>
    <Route path="/onboarding/student" element={<ProtectedRoute mode="auth"><StudentOnboarding /></ProtectedRoute>} />
    <Route path="/onboarding/student/preferences" element={<ProtectedRoute mode="auth"><StudentPreferences /></ProtectedRoute>} />
    <Route path="/onboarding/tutor" element={<ProtectedRoute mode="tutor-onboarding"><TutorOnboarding /></ProtectedRoute>} />
    <Route path="/onboarding/pending" element={<ProtectedRoute mode="auth"><PendingApproval /></ProtectedRoute>} />

    <Route path="/student/classroom" element={<ProtectedRoute mode="student"><StudentClassroom /></ProtectedRoute>} />
    <Route path="/student/courses" element={<ProtectedRoute mode="student"><StudentCourses /></ProtectedRoute>} />
    <Route path="/student/flashcards" element={<ProtectedRoute mode="student"><StudentFlashcards /></ProtectedRoute>} />
    <Route path="/student/flashcards/browse/:deckId" element={<ProtectedRoute mode="student"><FlashcardsBrowse /></ProtectedRoute>} />
    <Route path="/student/marketplace" element={<ProtectedRoute mode="student"><StudentMarketplace /></ProtectedRoute>} />
    <Route path="/student/calendar" element={<ProtectedRoute mode="student"><StudentCalendarPage /></ProtectedRoute>} />
    <Route path="/student/settings" element={<ProtectedRoute mode="student"><StudentSettings /></ProtectedRoute>} />
    <Route path="/student/live-session/:lessonId" element={<ProtectedRoute mode="student"><StudentLiveSession /></ProtectedRoute>} />
    <Route path="/student/lessons/:lessonId/done" element={<ProtectedRoute mode="student"><LessonDone /></ProtectedRoute>} />
    <Route path="/student/practice-room" element={<ProtectedRoute mode="student"><StudentPracticeRoom /></ProtectedRoute>} />
    <Route path="/student/buy-credits" element={<ProtectedRoute mode="student"><StudentBuyCredits /></ProtectedRoute>} />
    <Route path="/student/find-tutors" element={<ProtectedRoute mode="student"><StudentFindTutors /></ProtectedRoute>} />
    <Route path="/student/tutor/:tutorId" element={<ProtectedRoute mode="student"><TutorProfile /></ProtectedRoute>} />
    <Route path="/student/course/:courseId" element={<ProtectedRoute mode="student"><StudentCoursePlayer /></ProtectedRoute>} />
    <Route path="/student/puzzles" element={<ProtectedRoute mode="student"><FeatureRoute flag="puzzle_playground"><StudentPuzzles /></FeatureRoute></ProtectedRoute>} />
    <Route path="/student/puzzles/:packId" element={<ProtectedRoute mode="student"><FeatureRoute flag="puzzle_playground"><StudentPuzzlePlayer /></FeatureRoute></ProtectedRoute>} />

    <Route path="/tutor/classroom" element={<ProtectedRoute mode="tutor"><TutorClassroom /></ProtectedRoute>} />
    <Route path="/tutor/courses" element={<ProtectedRoute mode="tutor"><TutorCourses /></ProtectedRoute>} />
    <Route path="/tutor/calendar" element={<ProtectedRoute mode="tutor"><TutorCalendarPage /></ProtectedRoute>} />
    <Route path="/tutor/settings" element={<ProtectedRoute mode="tutor"><TutorSettings /></ProtectedRoute>} />
    <Route path="/tutor/live-session/:lessonId" element={<ProtectedRoute mode="tutor"><TutorLiveSession /></ProtectedRoute>} />
    <Route path="/tutor/lessons/:lessonId/done" element={<ProtectedRoute mode="tutor"><LessonDone /></ProtectedRoute>} />
    <Route path="/tutor/practice-room" element={<ProtectedRoute mode="tutor"><TutorPracticeRoom /></ProtectedRoute>} />
    <Route path="/tutor/earnings" element={<ProtectedRoute mode="tutor"><TutorEarnings /></ProtectedRoute>} />
    <Route path="/tutor/tax-information" element={<ProtectedRoute mode="tutor"><TutorTaxInformation /></ProtectedRoute>} />
    <Route path="/tutor/students" element={<ProtectedRoute mode="tutor"><TutorStudents /></ProtectedRoute>} />
    <Route path="/tutor/students/:studentProfileId/practice" element={<ProtectedRoute mode="tutor"><FeatureRoute flag="student_practice"><TutorStudentPractice /></FeatureRoute></ProtectedRoute>} />
    <Route path="/tutor/reviews" element={<ProtectedRoute mode="tutor"><TutorReviews /></ProtectedRoute>} />
    <Route path="/tutor/affiliate" element={<ProtectedRoute mode="tutor"><TutorAffiliate /></ProtectedRoute>} />
    <Route path="/tutor/courses/create" element={<ProtectedRoute mode="tutor"><TutorCreateCourse /></ProtectedRoute>} />
    <Route path="/tutor/courses/:courseId/edit" element={<ProtectedRoute mode="tutor"><TutorCreateCourse /></ProtectedRoute>} />
    <Route path="/tutor/resources" element={<ProtectedRoute mode="tutor"><TutorResources /></ProtectedRoute>} />
    <Route path="/tutor/playground" element={<ProtectedRoute mode="tutor"><FeatureRoute flag="puzzle_playground"><TutorPlayground /></FeatureRoute></ProtectedRoute>} />
    <Route path="/tutor/community" element={<ProtectedRoute mode="tutor"><FeatureRoute flag="tutor_community"><TutorCommunity /></FeatureRoute></ProtectedRoute>} />
    <Route path="/tutor/community/:postId" element={<ProtectedRoute mode="tutor"><FeatureRoute flag="tutor_community"><TutorCommunityThread /></FeatureRoute></ProtectedRoute>} />

    <Route path="/book/:tutorProfileId" element={<ProtectedRoute mode="student"><BookClass /></ProtectedRoute>} />
    <Route path="/community" element={<ProtectedRoute mode="auth"><Community /></ProtectedRoute>} />
    <Route path="/messages" element={<ProtectedRoute mode="auth"><Messages /></ProtectedRoute>} />
    </Route>

    <Route path="*" element={<NotFound />} />
  </Routes>
);

export default AppRoutes;
